// Wraps any avatar in an animated frame ring based on the user's
// active_avatar_frame cosmetic. Drops to no decoration for "default" / null.
//
// Usage: <AvatarFrame frame={profile?.active_avatar_frame} size={64}><avatar/></AvatarFrame>
import React from "react";
import { motion } from "framer-motion";

const FRAMES = {
  gold_ring: {
    border: "2px solid rgba(255,215,0,0.85)",
    boxShadow: "0 0 14px rgba(255,215,0,0.55), inset 0 0 8px rgba(255,215,0,0.25)",
    pulse: { boxShadow: ["0 0 14px rgba(255,215,0,0.55)", "0 0 26px rgba(255,215,0,0.85)", "0 0 14px rgba(255,215,0,0.55)"] },
  },
  neon_pulse: {
    border: "2px solid rgba(0,229,255,0.85)",
    boxShadow: "0 0 14px rgba(0,229,255,0.55), inset 0 0 8px rgba(0,229,255,0.25)",
    pulse: { boxShadow: ["0 0 14px rgba(0,229,255,0.55)", "0 0 26px rgba(0,229,255,0.95)", "0 0 14px rgba(0,229,255,0.55)"] },
  },
  // Ambassador — unlocked at 10 redeemed invites. Emerald laurel with pulsing glow.
  ambassador: {
    border: "2.5px solid rgba(61,220,151,0.9)",
    boxShadow: "0 0 16px rgba(61,220,151,0.6), inset 0 0 10px rgba(61,220,151,0.3)",
    pulse: { boxShadow: ["0 0 16px rgba(61,220,151,0.6)", "0 0 32px rgba(61,220,151,0.95)", "0 0 16px rgba(61,220,151,0.6)"] },
  },
  // Legendary — unlocked at 25 redeemed invites. Rotating rainbow conic ring.
  legendary: {
    border: "none",
    background: "conic-gradient(from 0deg, #FFD700, #FF3D6E, #7B61FF, #00C2FF, #3DDC97, #FFD700)",
    padding: "3px",
    boxShadow: "0 0 22px rgba(255,215,0,0.5)",
    rotating: true,
  },
};

export default function AvatarFrame({ frame, size = 64, radius = "50%", children, className = "" }) {
  const cfg = frame && frame !== "default" ? FRAMES[frame] : null;
  if (!cfg) {
    return (
      <div className={className} style={{ width: size, height: size, borderRadius: radius, position: "relative" }}>
        {children}
      </div>
    );
  }
  // Rotating conic ring variant (legendary)
  if (cfg.rotating) {
    return (
      <motion.div
        className={className}
        style={{
          width: size, height: size, borderRadius: radius, position: "relative",
          background: cfg.background, padding: cfg.padding, boxShadow: cfg.boxShadow,
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
        data-testid={`avatar-frame-${frame}`}
      >
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
          style={{
            width: "100%", height: "100%", borderRadius: radius,
            overflow: "hidden", background: "#0B0B0B",
          }}
        >
          {children}
        </motion.div>
      </motion.div>
    );
  }
  return (
    <motion.div
      className={className}
      style={{
        width: size, height: size, borderRadius: radius, position: "relative",
        border: cfg.border, boxShadow: cfg.boxShadow, padding: 0,
      }}
      animate={cfg.pulse}
      transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      data-testid={`avatar-frame-${frame}`}
    >
      <div style={{ width: "100%", height: "100%", borderRadius: radius, overflow: "hidden" }}>
        {children}
      </div>
    </motion.div>
  );
}
